'use client'

import { useEffect, useState } from 'react'
import Image from 'next/image'

import { motion } from 'motion/react'
import { ArrowRight } from 'lucide-react'

import { Button } from '@/components/ui/button'

import { talentPoolCopy } from '@/app/talent-pool/copy'

const imgUploadSimple = '/assets/upload.svg'
const imgCrownSimple = '/assets/crown.svg'
const imgBriefcase = '/assets/briefcase.svg'
const imgLightbulb = '/assets/lightbulb.svg'

const stepIcons = [
  imgUploadSimple,
  imgCrownSimple,
  imgBriefcase,
  imgLightbulb,
]

type TalentPoolHowItWorksSectionProps = {
  onScrollToUpload: () => void
}

export function TalentPoolHowItWorksSection({
  onScrollToUpload,
}: TalentPoolHowItWorksSectionProps) {
  const copy = talentPoolCopy
  const [firstWord, ...restWords] = copy.landing.howItWorksTitle.split(' ')
  const restTitle = restWords.join(' ')
  const steps = copy.landing.howItWorksSteps.map((step, index) => ({
    ...step,
    icon: stepIcons[index] ?? stepIcons[0],
  }))
  const [activeStep, setActiveStep] = useState(0)

  useEffect(() => {
    if (activeStep >= steps.length) setActiveStep(0)
  }, [activeStep, steps.length])

  useEffect(() => {
    if (typeof window === 'undefined') return
    if (steps.length <= 1) return

    const prefersReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    ).matches
    if (prefersReducedMotion) return

    const interval = window.setInterval(() => {
      setActiveStep((current) => (current + 1) % steps.length)
    }, 2500)

    return () => window.clearInterval(interval)
  }, [steps.length])

  return (
    <section id="cara-kerja" className="pb-14">
      <div className="rounded-3xl border border-border-primary bg-white dark:bg-card px-5 py-10 sm:px-8">
        <div className="mx-auto flex w-full max-w-[664px] flex-col items-center gap-4 text-center">
          <div className="rounded-full border-[1.5px] border-surface-primary bg-white dark:bg-card px-3 py-2 text-sm text-text-body">
            {copy.landing.navLinks[0].label}
          </div>
          <h2 className="text-[32px] font-medium leading-[40px] tracking-[-0.02em] text-text-heading md:text-[48px] md:leading-[60px] md:tracking-[-0.04em]">
            <span className="inline-flex items-center bg-status-error-bg px-1">
              {firstWord}
            </span>{' '}
            {restTitle || firstWord}
          </h2>
          <p className="max-w-[478px] text-base leading-[26px] text-text-muted md:text-[20px] md:leading-[30px]">
            {copy.landing.howItWorksDescription}
          </p>
        </div>

        <div className="relative mt-12 w-full">
          <div className="pointer-events-none absolute left-1/2 top-9 hidden w-full max-w-[1063px] -translate-x-1/2 md:block">
            <div className="relative flex items-center justify-between px-7">
              <div className="absolute left-7 right-7 top-1/2 h-px -translate-y-1/2 bg-border-primary" />
              {steps.map((step) => (
                <span
                  key={`line-dot-${step.number}`}
                  className="relative z-10 h-2.5 w-2.5 rounded-full border border-border-primary bg-white dark:bg-card"
                />
              ))}
            </div>
          </div>

          <div className="flex w-full flex-col gap-4 pb-2 md:grid md:grid-cols-4 md:gap-6 md:pb-0">
            {steps.map((step, index) => {
              const isActive = index === activeStep

              return (
                <motion.div
                  key={step.number}
                  className="flex w-full flex-row items-center gap-3 rounded-2xl border border-border-primary bg-white dark:bg-card p-4 will-change-transform md:min-w-0 md:flex-col md:items-start md:gap-4 md:p-5"
                  animate={{
                    y: isActive ? -4 : 0,
                    scale: isActive ? 1.02 : 1,
                  }}
                  transition={{
                    type: 'spring',
                    stiffness: 400,
                    damping: 30,
                  }}
                >
                  <div className="shrink-0 rounded-lg bg-surface-primary p-3">
                    <Image
                      alt={`${step.title} icon`}
                      src={step.icon}
                      width={20}
                      height={20}
                      className="h-5 w-5"
                    />
                  </div>
                  <p className="text-[16px] font-medium leading-[26px] text-text-heading md:text-[18px] md:leading-[28px]">
                    {step.title}
                  </p>
                </motion.div>
              )
            })}
          </div>
        </div>

        <div className="mt-8 text-center">
          <Button
            type="button"
            variant="ghost"
            onClick={onScrollToUpload}
            className="text-[14px] font-semibold text-brand-primary hover:bg-brand-primary/10"
          >
            {copy.landing.howItWorksCta}
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </div>
    </section>
  )
}
